import { EventBus, GameEvents } from './EventBus';

export interface MouseState {
  x: number;
  y: number;
  normalizedX: number;
  normalizedY: number;
  isDown: boolean;
  button: number;
}

/**
 * Handles mouse and keyboard input, normalizes coordinates
 */
export class InputManager {
  private canvas: HTMLCanvasElement;
  private eventBus: EventBus;
  private mouse: MouseState = {
    x: 0,
    y: 0,
    normalizedX: 0,
    normalizedY: 0,
    isDown: false,
    button: -1,
  };

  constructor(canvas: HTMLCanvasElement, eventBus: EventBus) {
    this.canvas = canvas;
    this.eventBus = eventBus;
    this.setupEventListeners();
  }

  private setupEventListeners(): void {
    this.canvas.addEventListener('mousemove', this.onMouseMove);
    this.canvas.addEventListener('mousedown', this.onMouseDown);
    this.canvas.addEventListener('mouseup', this.onMouseUp);
    this.canvas.addEventListener('mouseleave', this.onMouseLeave);
    this.canvas.addEventListener('contextmenu', this.onContextMenu);

    // Touch support
    this.canvas.addEventListener('touchstart', this.onTouchStart, {
      passive: false,
    });
    this.canvas.addEventListener('touchmove', this.onTouchMove, {
      passive: false,
    });
    this.canvas.addEventListener('touchend', this.onTouchEnd);

    // Resize handling
    window.addEventListener('resize', this.onResize);
  }

  private updateMousePosition(clientX: number, clientY: number): void {
    const rect = this.canvas.getBoundingClientRect();
    this.mouse.x = clientX - rect.left;
    this.mouse.y = clientY - rect.top;

    // Normalized coordinates (-1 to 1)
    this.mouse.normalizedX = (this.mouse.x / rect.width) * 2 - 1;
    this.mouse.normalizedY = -(this.mouse.y / rect.height) * 2 + 1;
  }

  private onMouseMove = (event: MouseEvent): void => {
    this.updateMousePosition(event.clientX, event.clientY);
    this.eventBus.emit('input:mousemove', { ...this.mouse });
  };

  private onMouseDown = (event: MouseEvent): void => {
    this.mouse.isDown = true;
    this.mouse.button = event.button;
    this.updateMousePosition(event.clientX, event.clientY);
    this.eventBus.emit('input:mousedown', { ...this.mouse });
  };

  private onMouseUp = (event: MouseEvent): void => {
    this.mouse.isDown = false;
    this.updateMousePosition(event.clientX, event.clientY);
    this.eventBus.emit('input:mouseup', { ...this.mouse });
    this.mouse.button = -1;
  };

  private onMouseLeave = (): void => {
    this.mouse.isDown = false;
    this.mouse.button = -1;
    this.eventBus.emit('input:mouseleave', { ...this.mouse });
  };

  private onContextMenu = (event: MouseEvent): void => {
    event.preventDefault();
  };

  private onTouchStart = (event: TouchEvent): void => {
    event.preventDefault();
    if (event.touches.length > 0) {
      const touch = event.touches[0];
      this.updateMousePosition(touch.clientX, touch.clientY);
      this.mouse.isDown = true;
      this.mouse.button = 0;
      this.eventBus.emit('input:mousedown', { ...this.mouse });
    }
  };

  private onTouchMove = (event: TouchEvent): void => {
    event.preventDefault();
    if (event.touches.length > 0) {
      const touch = event.touches[0];
      this.updateMousePosition(touch.clientX, touch.clientY);
      this.eventBus.emit('input:mousemove', { ...this.mouse });
    }
  };

  private onTouchEnd = (): void => {
    this.mouse.isDown = false;
    this.mouse.button = -1;
    this.eventBus.emit('input:mouseup', { ...this.mouse });
  };

  private onResize = (): void => {
    this.eventBus.emit(GameEvents.WINDOW_RESIZED, {
      width: window.innerWidth,
      height: window.innerHeight,
    });
  };

  /**
   * Get current mouse state
   */
  getMouseState(): Readonly<MouseState> {
    return this.mouse;
  }

  /**
   * Clean up event listeners
   */
  dispose(): void {
    this.canvas.removeEventListener('mousemove', this.onMouseMove);
    this.canvas.removeEventListener('mousedown', this.onMouseDown);
    this.canvas.removeEventListener('mouseup', this.onMouseUp);
    this.canvas.removeEventListener('mouseleave', this.onMouseLeave);
    this.canvas.removeEventListener('contextmenu', this.onContextMenu);
    this.canvas.removeEventListener('touchstart', this.onTouchStart);
    this.canvas.removeEventListener('touchmove', this.onTouchMove);
    this.canvas.removeEventListener('touchend', this.onTouchEnd);
    window.removeEventListener('resize', this.onResize);
  }
}
